import { ErrorBoundary, ModalStackContext } from '@dixa/lib-shared-habla';
import { accessControl } from 'lib-shared-access-control';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  VoidFunctionComponent,
} from 'react';

import { appActions } from 'app/app.actions';
import { AppModal, AppModalType } from 'app/types/appModal.types';
import { featureFlags } from 'utils/featureFlagging/featureFlags';
import { kebabToPascalCase } from 'utils/formatting/string';
import { pubSub } from 'utils/pubSub';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

import { createAppModalContext } from '../appModalContext/appModalContext';
import { AppModalContextValue } from '../appModalContext/appModalContext.types';

import { CreateAppModalOptions } from './createAppModal.types';

/**
 * A higher-order component that handles rendering an app modal based on its
 * visibility state and optional feature flag.
 *
 * Use this higher-order component to create lazy app modals that should not
 * mount until they're invoked by the user, in order to avoid unnecessary
 * rendering and code execution, such as effects which might make an API call.
 *
 * The second argument i.e. the modal component implementation can either be a
 * named function, in which case the `displayName` of the component will be
 * inferred correctly, or an anonymous function, in which case a `displayName`
 * will be generated from the modal `type`, in order for the component to be
 * correctly displayed in the React DevTools.
 *
 * @example
 *   import { createAppModal } from 'utils/modal';
 *
 *   export const MyModal = createAppModal(
 *     { type: 'app:my-modal', featureFlag: 'is-my-modal-enabled' },
 *     ({ isOpen, modal, hide }) => {
 *       return (
 *         <Modal isOpen={isOpen} onClose={hide}>
 *           <ModalHead title={modal.arg.title} />
 *         </Modal>
 *       );
 *     },
 *   );
 */
export function createAppModal<TAppModalType extends AppModalType>(
  { type, featureFlag, ...options }: CreateAppModalOptions<TAppModalType>,
  WrappedComponent: VoidFunctionComponent<
    AppModalContextValue<TAppModalType, AppModal>
  >,
): VoidFunctionComponent {
  const displayName =
    WrappedComponent.displayName ||
    WrappedComponent.name ||
    kebabToPascalCase(type.replaceAll(':', '-')) + 'Modal';

  const Component = (): JSX.Element | null => {
    const dispatch = useAppDispatch();
    const modals = useAppSelector((state) => state.app.modals);
    const [meta, setMeta] = useState<AppModal['meta']>();

    const handleHide = useCallback(() => {
      dispatch(appActions.hideModal({ type }));
    }, [dispatch]);

    // Listens to when the show modal event gets published in order to obtain the
    // meta so that this can get returned to the modal implementations and in turn
    // get resolved/rejected.
    useEffect(() => {
      const unsubscribe = pubSub.subscribe('showModal', (modal) => {
        if (modal.type === type) {
          setMeta(modal.meta);
        }
      });

      return unsubscribe;
    }, []);

    /**
     * The value of the modal context, which is also injected as props into the
     * wrapped modal component, so the consumer has direct access to the
     * functionality. Alternatively the consumer can choose to use the
     * `useAppModal` hook to access the same deeper in the component tree.
     */
    const modalContextValue = useMemo((): AppModalContextValue<
      TAppModalType,
      AppModal
    > | null => {
      if (!modals.length) return null;

      // The modal is the first one in the stack, so it is visible.
      const [firstModal] = modals;
      if (firstModal.type === type) {
        return {
          isOpen: true,
          modal: firstModal,
          meta,
          hide: handleHide,
        } as AppModalContextValue<TAppModalType, AppModal>;
      }

      // The modal is in the stack, but it's not the first one, therefore it's not visible.
      const hiddenModal = modals.find((modal) => modal.type === type);
      if (hiddenModal) {
        return {
          isOpen: false,
          modal: hiddenModal,
          meta,
        } as AppModalContextValue<TAppModalType, AppModal>;
      }

      return null;
    }, [handleHide, meta, modals]);

    const hasFeature = useMemo(
      () =>
        featureFlag
          ? Object.values(featureFlags.get([featureFlag])).every(Boolean)
          : true,
      [],
    );

    const hasAccess = options.accessControl
      ? accessControl.hasAccess(
          ...(options.accessControl as Parameters<
            typeof accessControl.hasAccess
          >),
        )
      : true;

    if (!hasFeature || !hasAccess || !modalContextValue) return null;

    const AppModalContext = createAppModalContext<TAppModalType>();
    const stacked =
      !options.noGhostPanel &&
      modalContextValue.isOpen &&
      modals[0].type === type &&
      modals.length > 1;

    return (
      <ErrorBoundary fallback={null}>
        <ModalStackContext.Provider value={stacked}>
          <AppModalContext.Provider value={modalContextValue}>
            <WrappedComponent {...modalContextValue} />
          </AppModalContext.Provider>
        </ModalStackContext.Provider>
      </ErrorBoundary>
    );
  };

  WrappedComponent.displayName = displayName;
  Component.displayName = `createAppModal(${displayName})`;

  return Component;
}
